
import Vue from "vue";
import { ElementIdService, focus } from "@fkui/logic";
import { FFileSelector, FIcon, FMessageBox } from "@fkui/vue";
import { UploadedFile } from "@/store/types";
import { mapFields } from "vuex-map-fields";
import TextFileItem from "@/components/Observationer/TextFileItem.vue";

enum ERROR {
    OK = 0,
    TOTAL_TOO_LARGE = 1,
    FILE_TOO_LARGE = 2,
    WRONG_FILE_TYPE = 3,
    COMBINED_ERROR = 4,
}

interface AcceptedFileType {
    mime: string;
    extension: string;
}

class UploadError {
    public header: string;
    public message: string;
    public showAllowedFileTypes: boolean;

    public constructor(header: string, message: string, options: { showAllowedFileTypes: boolean }) {
        this.header = header;
        this.message = message;
        this.showAllowedFileTypes = options.showAllowedFileTypes;
    }
}

const ERROR_MAP = {
    [ERROR.TOTAL_TOO_LARGE]: new UploadError(
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.rubrik.filernaforstora",
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.ingress.filernaforstora",
        { showAllowedFileTypes: false },
    ),
    [ERROR.FILE_TOO_LARGE]: new UploadError(
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.rubrik.forstorfil",
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.ingress.forstorfil",
        { showAllowedFileTypes: false },
    ),
    [ERROR.WRONG_FILE_TYPE]: new UploadError(
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.rubrik.felformat",
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.ingress.felformat",
        { showAllowedFileTypes: true },
    ),
    [ERROR.COMBINED_ERROR]: new UploadError(
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.rubrik.kombineradefel",
        "impulskontrollutredning.anmalan.generell.filuppladdare.felmeddelande.ingress.kombineradefel",
        { showAllowedFileTypes: true },
    ),
};

const ACCEPTED_FILE_TYPES: AcceptedFileType[] = [
    { mime: "application/pdf", extension: "pdf" },
    { mime: "image/jpeg", extension: "jpeg" },
    { mime: "image/jpeg", extension: "jpg" },
    { mime: "image/png", extension: "png" },
    { mime: "image/tiff", extension: "tiff" },
    { mime: "image/tiff", extension: "tif" },
    { mime: "image/gif", extension: "gif" },
    { mime: "image/bmp", extension: "bmp" },
    { mime: "application/msword", extension: "doc" },
    { mime: "application/msword", extension: "dot" },
    { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", extension: "docx" },
    { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.template", extension: "dotx" },
];

export default Vue.extend({
    name: "FileUploader",
    components: {
        FFileSelector,
        FMessageBox,
        FIcon,
        TextFileItem,
    },
    props: {
        /**
         * The id for the input id attribute.
         * The id for the label for attribute.
         * If the prop is not set a random value will be generated.
         */
        id: {
            type: String,
            required: false,
            default: () => ElementIdService.generateElementId(),
        },
        /**
         * Max combined files size (bytes).
         */
        maxCombinedFilesSize: {
            type: Number,
            required: false,
            default: 20000000,
        },
        /**
         * Max file size (bytes).
         */
        maxFileSize: {
            type: Number,
            required: false,
            default: 10000000,
        },
        /**
         * File types supported.
         */
        allowedFileTypes: {
            type: Array,
            required: false,
            default: () => {
                return ACCEPTED_FILE_TYPES.map((e) => e.extension);
            },
        },
        /**
         * Mime types allowed. No duplicates from ACCEPTED_FILE_TYPES will be used here.
         */
        allowedMimeTypes: {
            type: String,
            required: false,
            default: [...new Set(ACCEPTED_FILE_TYPES.map((e) => e.mime))].join(", "),
        },
    },

    data: () => ({
        currentIndex: 1,
        currentStatus: ERROR.OK,
        errorMap: ERROR_MAP,
        attemptedFileUploadSize: 0,
    }),
    computed: {
        ...mapFields(["observationer.files"]),
        screenreaderText(): string {
            return `${this.$t("SBRV.RW.bilaga.uppladdad")}`;
        },
        attemptedTotalUploadedSize(): string {
            return ((this.totalUploadedSize() + this.attemptedFileUploadSize) / 1000000).toFixed(2).toString();
        },
        tooLargeFileSize(): string {
            return (this.attemptedFileUploadSize / 1000000).toFixed(2).toString();
        },
    },
    methods: {
        translateError(status: ERROR): string {
            if (status === ERROR.OK) {
                return "";
            }

            const errorMessage = this.errorMap[status].message;
            return this.$t(errorMessage, { mb: this.tooLargeFileSize, total: this.attemptedTotalUploadedSize })
                .replace(/\r\n/g, "\n")
                .split("\n\n")
                .map((line) => `<p class="message-box__text">${line}</p>`)
                .join("\n");
        },
        async onFileSelected(fileList: FileList): Promise<void> {
            const file = fileList.item(0);
            if (!file) {
                return;
            }

            const currentFile: UploadedFile = {
                // File id come from response, for now just set it to unique value. Will be -1, -2, -3 and so on.
                id: (this.currentIndex * -1).toString(),
                file,
            };

            this.pushFile(currentFile);
        },
        // eslint-disable-next-line complexity
        async pushFile(currentFile: UploadedFile): Promise<void> {
            this.currentStatus = ERROR.OK;
            let nrOfErrors = 0;
            if (!currentFile.file.type || !this.allowedMimeTypes.includes(currentFile.file.type)) {
                this.currentStatus = ERROR.WRONG_FILE_TYPE;
                nrOfErrors++;
            }
            if (currentFile.file.size > this.maxFileSize) {
                this.currentStatus = ERROR.FILE_TOO_LARGE;
                this.attemptedFileUploadSize = currentFile.file.size;
                nrOfErrors++;
            }
            if (this.totalUploadedSize() + currentFile.file.size > this.maxCombinedFilesSize) {
                this.currentStatus = ERROR.TOTAL_TOO_LARGE;
                this.attemptedFileUploadSize = currentFile.file.size;
                nrOfErrors++;
            }

            if (nrOfErrors > 1) {
                this.currentStatus = ERROR.COMBINED_ERROR;
            }

            if (nrOfErrors > 0) {
                await this.$nextTick();
                const fileErrorHeader = this.$refs.fileErrorHeader;
                if (fileErrorHeader instanceof HTMLElement) {
                    focus(fileErrorHeader, true);
                }
                return;
            }

            this.currentIndex++;

            this.$store.commit("addFile", currentFile);

            await this.$nextTick();
            const element: HTMLElement | null = document.getElementById(currentFile.id);
            if (element) {
                element.focus();
                this.currentStatus = ERROR.OK;
            }
        },
        async removeFile(id: string): Promise<void> {
            this.$store.commit("removeFile", id);
            await this.$nextTick();
            this.currentStatus = ERROR.OK;
            this.focusOnElement();
        },
        focusOnElement(): void {
            const fileSelector = (this.$refs.fileSelector as Vue).$el.querySelector<HTMLElement>("label.button");
            if (fileSelector) {
                fileSelector.focus();
            }
        },
        totalUploadedSize(): number {
            const sizes = this.$store.state.observationer.files.map((f: UploadedFile) => f.file.size);
            return sizes.reduce((total: number, cur: number) => total + cur, 0);
        },
    },
});
